/**
 * API client for Expandly AI Portal backend
 */
import axios, { AxiosInstance, AxiosError } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'

class APIClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 180000, // 3 minutes for AI queries
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth headers if needed
        const userId = localStorage.getItem('userId') || 'anonymous'
        config.headers['X-User-ID'] = userId
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        console.error('API Error:', error.response?.data || error.message)
        return Promise.reject(error)
      }
    )
  }

  // Query endpoints
  async executeQuery(query: string, includeInsights: boolean = true) {
    const response = await this.client.post('/query/', {
      query,
      include_insights: includeInsights,
      user_id: localStorage.getItem('userId') || 'system',
    })
    return response.data
  }

  async getSuggestedQueries() {
    const response = await this.client.get('/query/suggestions')
    return response.data
  }

  // Market endpoints
  async getMarketOpportunities(limit: number = 10, minScore: number = 6.0) {
    const response = await this.client.get('/markets/opportunities', {
      params: { limit, min_score: minScore },
    })
    return response.data
  }

  async getMarketDetails(country: string) {
    const response = await this.client.get(`/markets/${country}`)
    return response.data
  }

  async generateExpansionStrategy(params: {
    current_markets: string[]
    product_categories: string[]
    budget_range: { min: number; max: number }
    timeline: string
  }) {
    const response = await this.client.post('/markets/strategy', params)
    return response.data
  }

  // Report endpoints
  async generateReport(year: number, quarter: number) {
    const response = await this.client.post('/reports/generate', {
      year,
      quarter,
      user_id: localStorage.getItem('userId') || 'system',
      include_charts: true,
      format: 'json',
    })
    return response.data
  }

  async getReport(reportId: string) {
    const response = await this.client.get(`/reports/${reportId}`)
    return response.data
  }

  async getAvailableQuarters() {
    const response = await this.client.get('/reports/quarters/available')
    return response.data
  }

  // Health endpoint
  async checkHealth() {
    const response = await this.client.get('/health/detailed')
    return response.data
  }

  // Generic HTTP methods for direct access
  async get(url: string, config?: any) {
    return this.client.get(url, config)
  }

  async post(url: string, data?: any, config?: any) {
    return this.client.post(url, data, config)
  }

  async put(url: string, data?: any, config?: any) {
    return this.client.put(url, data, config)
  }

  async delete(url: string, config?: any) {
    return this.client.delete(url, config)
  }
}

export const apiClient = new APIClient()

// Named exports for hooks
export const executeQuery = (request: { query: string; include_insights?: boolean; user_id?: string }) =>
  apiClient.executeQuery(request.query, request.include_insights)

export const getMarketOpportunities = (limit?: number, minScore?: number) =>
  apiClient.getMarketOpportunities(limit, minScore)

export const getMarketDetails = (country: string) =>
  apiClient.getMarketDetails(country)

export const getSuggestedQueries = () =>
  apiClient.getSuggestedQueries()

export const generateReport = (year: number, quarter: number) =>
  apiClient.generateReport(year, quarter)

export const getReport = (reportId: string) =>
  apiClient.getReport(reportId)

export const checkHealth = () =>
  apiClient.checkHealth()

export default apiClient
